import type { Message, ReceiptKind } from '@orbit-sync/shared-types';
import type { OrbitSocket } from '../lib/socket.js';
import { useChatStore } from './chatStore.js';

/**
 * This client's side of receipts: telling the server what has reached it and
 * what its user has read, so the people who sent those messages see ✓✓ and then
 * blue.
 *
 *   delivered - a message from someone else arrived over the socket
 *   read      - the conversation is on screen, in a visible tab, and its newest
 *               message from someone else is there to be seen
 *
 * The server moves a cursor only forwards, so a duplicate acknowledgement is
 * harmless - but each one is still a socket frame and a database write, so the
 * furthest point already acknowledged per channel is remembered here and
 * anything at or behind it is not sent again. Module state rather than React
 * state: nothing about it renders.
 */

const acknowledged = new Map<string, { delivered: string | null; read: string | null }>();

function alreadyCovered(channelId: string, kind: ReceiptKind, createdAt: string): boolean {
  const entry = acknowledged.get(channelId);

  if (!entry) {
    return false;
  }

  // Reading covers delivery, so for `delivered` either cursor counts.
  const covered = (cursor: string | null): boolean => cursor !== null && cursor >= createdAt;

  return kind === 'read' ? covered(entry.read) : covered(entry.read) || covered(entry.delivered);
}

function remember(channelId: string, kind: ReceiptKind, createdAt: string): void {
  const entry = acknowledged.get(channelId) ?? { delivered: null, read: null };
  acknowledged.set(channelId, { ...entry, [kind]: createdAt });
}

function acknowledge(socket: OrbitSocket, message: Message, kind: ReceiptKind): void {
  if (alreadyCovered(message.channelId, kind, message.createdAt)) {
    return;
  }

  remember(message.channelId, kind, message.createdAt);
  socket.emit('chat.receipt', { channelId: message.channelId, messageId: message.id, kind });
}

/** Whether the user can see the page at all. */
export function isDocumentVisible(): boolean {
  return document.visibilityState === 'visible';
}

/**
 * A message just arrived over the socket. Someone else's message is delivered
 * the moment it lands; if its conversation is the one on screen, it is read.
 */
export function acknowledgeIncoming(
  socket: OrbitSocket,
  message: Message,
  currentUserId: string | null,
): void {
  if (currentUserId === null || message.senderId === currentUserId) {
    return;
  }

  const watching =
    isDocumentVisible() && useChatStore.getState().activeChannelId === message.channelId;

  acknowledge(socket, message, watching ? 'read' : 'delivered');
}

/**
 * The conversation is on screen: mark its newest message from someone else as
 * read. Called when a channel opens, when its history lands, and when the tab
 * comes back into view.
 */
export function acknowledgeLatestRead(
  socket: OrbitSocket,
  channelId: string,
  currentUserId: string | null,
): void {
  if (currentUserId === null || !isDocumentVisible()) {
    return;
  }

  const items = useChatStore.getState().transcripts[channelId]?.items ?? [];

  for (let index = items.length - 1; index >= 0; index -= 1) {
    const message = items[index];

    if (message && message.senderId !== currentUserId) {
      acknowledge(socket, message, 'read');
      return;
    }
  }
}

/** Forget everything acknowledged, on sign-out, so the next account starts clean. */
export function resetAcknowledgements(): void {
  acknowledged.clear();
}
